import React, { useState } from "react";
import {
  EditButton,
  List,
  ShowButton,
  DeleteButton,
  CreateButton,
} from "@refinedev/mui";
import { useAuth } from "../../contexts/auth-context";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  IResourceComponentsProps,
  useTranslate,
  useCustom,
  HttpError,
  useApiUrl,
} from "@refinedev/core";
import {
  Box,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { format } from "date-fns";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";

export interface IPremiumDevice {
  id: number;
  macAddress: string;
  serialNumber?: string;
  description?: string;
  validityDays: number;
  isActive: boolean;
  activatedAt?: Date;
  activatedByUserId?: number;
  user?: {
    id: number;
    email: string;
    fullname?: string;
  };
}

interface BulkEditDialogProps {
  open: boolean;
  selectedIds: number[];
  onClose: () => void;
  onSuccess: () => void;
  apiUrl: string;
}

interface CsvBulkEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiUrl: string;
}

const BulkEditDialog: React.FC<BulkEditDialogProps> = ({
  open,
  selectedIds,
  onClose,
  onSuccess,
  apiUrl,
}) => {
  const [description, setDescription] = useState("");
  const [validityDays, setValidityDays] = useState("");
  const [activeStatus, setActiveStatus] = useState<"keep" | "active" | "inactive">("keep");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    const updates: any = {};
    if (description.trim()) updates.description = description.trim();
    if (validityDays) updates.validityDays = parseInt(validityDays);
    if (activeStatus === "active") updates.isActive = true;
    if (activeStatus === "inactive") updates.isActive = false;

    if (Object.keys(updates).length === 0) {
      setError("Please select at least one field to update");
      setLoading(false);
      return;
    }

    try {
      await axios.patch(`${apiUrl}/premium-devices/bulk`, {
        ids: selectedIds,
        updates,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update devices");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Bulk Edit {selectedIds.length} Device{selectedIds.length > 1 ? "s" : ""}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="info">
            Only fill in the fields you want to update. Empty fields will be ignored.
          </Alert>

          <TextField
            label="Batch Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            placeholder="Leave empty to keep current values"
            helperText="Set a new description for all selected devices"
          />

          <TextField
            label="Validity Duration (days)"
            value={validityDays}
            onChange={(e) => setValidityDays(e.target.value)}
            type="number"
            fullWidth
            placeholder="Leave empty to keep current values"
            helperText="Set a new validity duration for all selected devices"
            InputProps={{
              inputProps: { min: 1, max: 3650 },
            }}
          />

          <FormControl component="fieldset">
            <FormLabel component="legend">Device Active Status</FormLabel>
            <RadioGroup
              value={activeStatus}
              onChange={(e) => setActiveStatus(e.target.value as "keep" | "active" | "inactive")}
            >
              <FormControlLabel
                value="keep"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">Keep current status</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Don't change the active status
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="active"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">Set all to Active</Typography>
                    <Typography variant="caption" color="text.secondary">
                      All selected devices will be activated
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="inactive"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">Set all to Inactive</Typography>
                    <Typography variant="caption" color="text.secondary">
                      All selected devices will be deactivated
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
        >
          {loading ? "Updating..." : "Update Devices"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CsvBulkEditDialog: React.FC<CsvBulkEditDialogProps> = ({
  open,
  onClose,
  onSuccess,
  apiUrl,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preview, setPreview] = useState<any[]>([]);

  const downloadTemplate = () => {
    // Create CSV template with sample data (using semicolon for European format)
    const template = `id;macAddress;serialNumber;description;validityDays;isActive
1;AA:BB:CC:DD:EE:FF;SN-2025-00001;Commercial Contract Q1 2025;30;true
2;11:22:33:44:55:66;SN-2025-00002;Partner Device Batch;90;true
3;FF:EE:DD:CC:BB:AA;SN-2025-00003;Test Device;30;false`;

    // Create a blob and download it
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'premium_devices_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Detect CSV delimiter (comma or semicolon)
  const detectDelimiter = (text: string): string => {
    const firstLine = text.split('\n')[0];
    const commaCount = (firstLine.match(/,/g) || []).length;
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    return semicolonCount > commaCount ? ';' : ',';
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError("Please select a CSV file");
        return;
      }
      setFile(selectedFile);
      setError("");
      setSuccess("");
      parseCSVPreview(selectedFile);
    }
  };

  const parseCSVPreview = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const delimiter = detectDelimiter(text);
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(delimiter).map(h => h.trim());

      // Show first 5 rows as preview
      const previewData = lines.slice(1, 6).map(line => {
        const values = line.split(delimiter).map(v => v.trim());
        const obj: any = {};
        headers.forEach((header, index) => {
          obj[header] = values[index];
        });
        return obj;
      });

      setPreview(previewData);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const delimiter = detectDelimiter(text);
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          setError("CSV file is empty or invalid");
          setLoading(false);
          return;
        }

        const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase());

        // Validate required columns
        if (!headers.includes('id') && !headers.includes('macaddress')) {
          setError("CSV must contain either 'id' or 'macAddress' column to identify devices");
          setLoading(false);
          return;
        }

        const updates = lines.slice(1).map(line => {
          const values = line.split(delimiter).map(v => v.trim());
          const obj: any = {};
          headers.forEach((header, index) => {
            if (values[index]) {
              const value = values[index];

              // Convert types for specific fields (header is already lowercase)
              if (header === 'validitydays') {
                obj[header] = parseInt(value, 10);
              } else if (header === 'id') {
                obj[header] = parseInt(value, 10);
              } else if (header === 'isactive') {
                obj[header] = value.toLowerCase() === 'true' || value === '1';
              } else {
                obj[header] = value;
              }
            }
          });
          return obj;
        });

        console.log('Updates being sent:', JSON.stringify(updates, null, 2));

        await axios.patch(`${apiUrl}/premium-devices/bulk-csv`, { updates });

        setSuccess(`Successfully updated ${updates.length} device(s)`);
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to update devices");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Edit via CSV Import</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Alert severity="info">
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              CSV Format Requirements:
            </Typography>
            <Typography variant="body2" component="div">
              • First column must be either <code>id</code> or <code>macAddress</code> to identify devices
              <br />
              • Optional columns: <code>description</code>, <code>validityDays</code>, <code>isActive</code>
              <br />
              • Example: <code>id,description,validityDays,isActive</code>
              <br />
              • Only include columns you want to update
            </Typography>
          </Alert>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            fullWidth
            color="secondary"
          >
            Download CSV Template
          </Button>

          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            fullWidth
          >
            {file ? file.name : "Select CSV File"}
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileChange}
            />
          </Button>

          {preview.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ color: "text.primary", fontWeight: 600 }}>
                Preview (first 5 rows):
              </Typography>
              <Box sx={{
                maxHeight: 400,
                overflow: "auto",
                border: "2px solid #e0e0e0",
                borderRadius: 2,
                bgcolor: "#fff"
              }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px"
                }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      {Object.keys(preview[0]).map((header) => (
                        <th key={header} style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          borderBottom: "2px solid #ddd",
                          fontWeight: 600,
                          color: "#000",
                          whiteSpace: "nowrap"
                        }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, index) => (
                      <tr key={index} style={{
                        backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa"
                      }}>
                        {Object.values(row).map((value: any, colIndex) => (
                          <td key={colIndex} style={{
                            padding: "12px 16px",
                            borderBottom: "1px solid #e0e0e0",
                            color: "#000",
                            whiteSpace: "nowrap"
                          }}>
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !file}
          startIcon={loading ? <CircularProgress size={20} /> : <UploadFileIcon />}
        >
          {loading ? "Updating..." : "Import & Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PremiumDeviceList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const apiUrl = useApiUrl();
  const { user } = useAuth();

  // SAFEE_STAFF can view/edit single devices but not bulk operations or delete
  const canBulkModify = user?.role === "ADMIN";
  const canDelete = user?.role === "ADMIN";

  const [searchTerm, setSearchTerm] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  const { data, isLoading, refetch } = useCustom<IPremiumDevice[]>({
    url: `${apiUrl}/premium-devices`,
    method: "get",
    config: {
      query: searchTerm ? { q: searchTerm } : {},
    },
    queryOptions: {
      queryKey: ["premium-devices", searchTerm],
    },
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleOpenBulkEdit = () => {
    setBulkEditOpen(true);
  };

  const handleCloseBulkEdit = () => {
    setBulkEditOpen(false);
  };

  const handleBulkEditSuccess = () => {
    setSelectedIds([]);
    setBulkEditOpen(false);
    refetch();
  };

  const handleCsvImportSuccess = () => {
    setCsvImportOpen(false);
    refetch();
  };

  const columns = React.useMemo<GridColDef<IPremiumDevice>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        type: "number",
        width: 70,
      },
      {
        field: "macAddress",
        headerName: "MAC Address",
        minWidth: 150,
        flex: 0.8,
        renderCell: ({ row }) => (
          <Typography variant="body2" fontFamily="monospace" fontSize="12px">
            {row.macAddress}
          </Typography>
        ),
      },
      {
        field: "serialNumber",
        headerName: "Serial Number",
        minWidth: 150,
        flex: 0.8,
        renderCell: ({ row }) => (
          <Typography variant="body2" fontFamily="monospace" fontSize="12px">
            {row.serialNumber || "-"}
          </Typography>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 150,
        flex: 1,
        renderCell: ({ row }) => row.description || "-",
      },
      {
        field: "validityDays",
        headerName: "Validity",
        width: 120,
        type: "number",
        renderCell: ({ row }) => {
          const years = Math.floor(row.validityDays / 365);
          const months = Math.floor((row.validityDays % 365) / 30);
          let duration = "";
          if (years > 0) duration += `${years}y `;
          if (months > 0) duration += `${months}m`;
          return (
            <Box>
              <Typography variant="body2" fontSize="13px">{row.validityDays} days</Typography>
              <Typography variant="caption" color="text.secondary" fontSize="11px">
                ({duration.trim()})
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "isActive",
        headerName: "Status",
        width: 90,
        renderCell: ({ row }) =>
          row.isActive ? (
            <Chip
              label="Active"
              size="small"
              color="success"
              icon={<CheckCircleIcon />}
            />
          ) : (
            <Chip
              label="Inactive"
              size="small"
              color="error"
              icon={<CancelIcon />}
            />
          ),
      },
      {
        field: "status",
        headerName: "Activation",
        width: 110,
        renderCell: ({ row }) =>
          row.activatedByUserId ? (
            <Chip
              label="Activated"
              size="small"
              color="primary"
              variant="outlined"
            />
          ) : (
            <Chip
              label="Not Activated"
              size="small"
              color="default"
              variant="outlined"
            />
          ),
      },
      {
        field: "activatedAt",
        headerName: "Activated At",
        width: 150,
        renderCell: ({ row }) =>
          row.activatedAt
            ? format(new Date(row.activatedAt), "yyyy-MM-dd HH:mm")
            : "-",
      },
      {
        field: "user",
        headerName: "User",
        minWidth: 200,
        flex: 1,
        renderCell: ({ row }) =>
          row.user ? (
            <Box>
              <Typography variant="body2" fontSize="13px">{row.user.fullname || row.user.email}</Typography>
              <Typography variant="caption" color="text.secondary" fontSize="11px">
                {row.user.email}
              </Typography>
            </Box>
          ) : (
            "-"
          ),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        width: 150,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <ShowButton hideText recordItemId={row.id} size="small" />
            <EditButton hideText recordItemId={row.id} size="small" />
            {canDelete && (
              <DeleteButton
                hideText
                recordItemId={row.id}
                size="small"
                confirmTitle={`Delete device ${row.macAddress}?`}
                confirmOkText="Delete"
                confirmCancelText="Cancel"
              />
            )}
          </Box>
        ),
      },
    ],
    [canDelete]
  );

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          {/* Bulk operations are restricted to ADMIN only - SAFEE_STAFF cannot use them */}
          {canBulkModify && selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleOpenBulkEdit}
            >
              Bulk Edit ({selectedIds.length})
            </Button>
          )}
          {canBulkModify && (
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setCsvImportOpen(true)}
            >
              CSV Import
            </Button>
          )}
          {canBulkModify && <CreateButton>Create Batch</CreateButton>}
        </>
      )}
    >
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by MAC address, serial number, description, or user email..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>
      <DataGrid
        rows={data?.data || []}
        columns={columns}
        loading={isLoading}
        autoHeight
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="client"
        checkboxSelection
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={(newSelection) => {
          setSelectedIds(newSelection as number[]);
        }}
      />

      <BulkEditDialog
        open={bulkEditOpen}
        selectedIds={selectedIds}
        onClose={handleCloseBulkEdit}
        onSuccess={handleBulkEditSuccess}
        apiUrl={apiUrl}
      />

      <CsvBulkEditDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onSuccess={handleCsvImportSuccess}
        apiUrl={apiUrl}
      />
    </List>
  );
};
